import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchSeenTours, markTourSeen } from '@apis/preferences';
import { OWNS_ERROR_FEEDBACK } from '@d-orca/shared/client/hooks';

export type TourSeenState = 'unknown' | 'seen' | 'unseen';

export const SEEN_TOURS_QUERY_KEY = ['tours', 'seen'] as const;

/**
 * Whether one page tour has been dismissed by this user.
 *
 * `unknown` covers both "still loading" and "failed to load": a tour that
 * opens itself over a page is only welcome once, so with no answer the page
 * must not auto-start it. The profile menu's Page tour item does not read this
 * state at all, so a failed read costs nothing but the auto-start.
 *
 * `markSeen` is optimistic: the tour has already closed when it fires, and a
 * re-open a second later because the write was slow would be the one bug this
 * feature must never have.
 */
export function useTourSeen(tourId: string): { state: TourSeenState; markSeen: () => void } {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: SEEN_TOURS_QUERY_KEY,
    queryFn: ({ signal }) => fetchSeenTours(signal),
    staleTime: Infinity,
  });

  const mutation = useMutation({
    // Silent by design: an unsaved dismissal only means the tour offers itself again.
    meta: OWNS_ERROR_FEEDBACK,
    mutationFn: () => markTourSeen(tourId),
    onMutate: () => {
      queryClient.setQueryData<string[]>(SEEN_TOURS_QUERY_KEY, (previous = []) =>
        previous.includes(tourId) ? previous : [...previous, tourId],
      );
    },
    onSuccess: (seen) => queryClient.setQueryData(SEEN_TOURS_QUERY_KEY, seen),
  });

  const state: TourSeenState =
    query.data === undefined ? 'unknown' : query.data.includes(tourId) ? 'seen' : 'unseen';

  const { mutate } = mutation;
  const markSeen = useCallback(() => mutate(), [mutate]);

  return { state, markSeen };
}
